import { Quaternion, Scene, TransformNode, Vector3 } from '@babylonjs/core';
import { RAPIER, type PhysicsWorld } from '@roadworld/physics';
import type { PlayerIdentity, PlayerSnapshot } from '@roadworld/shared';
import { createChassisMesh, createWheelMesh } from '../vehicle/chassisFactory.js';
import { VehicleTuning } from '../vehicle/tuning.js';
import { SnapshotBuffer } from './SnapshotBuffer.js';

/**
 * One remote player in the world. Owns:
 *   - kinematic Rapier body + chassis collider (so the local dynamic car bumps into it),
 *   - chassis + wheel visuals (cloned mesh hierarchy in the player's color),
 *   - snapshot buffer driving interpolated motion.
 *
 * The kinematic body is set via `setNextKinematicTranslation` / `setNextKinematicRotation`
 * each frame from the interpolated pose.
 */
export class RemotePlayer {
  readonly identity: PlayerIdentity;
  private physics: PhysicsWorld;
  private body: RAPIER.RigidBody;
  // Collider is retained as an owned resource even though we don't read from it after setup.
  private root: TransformNode;
  private bodyMesh: TransformNode;
  private wheels: TransformNode[] = [];
  buffer = new SnapshotBuffer();
  /** Accumulated wheel rotation around the spin axis (radians). */
  private wheelSpin = 0;
  private disposed = false;

  constructor(physics: PhysicsWorld, scene: Scene, identity: PlayerIdentity) {
    this.physics = physics;
    this.identity = identity;

    const T = VehicleTuning;

    // Kinematic position-based body — moves only via setNextKinematicTranslation.
    const bodyDesc = RAPIER.RigidBodyDesc.kinematicPositionBased()
      .setTranslation(0, -1000, 0) // park far below until first snapshot arrives
      .setCcdEnabled(true);
    this.body = physics.world.createRigidBody(bodyDesc);

    const colliderDesc = RAPIER.ColliderDesc.cuboid(
      T.chassisWidth * 0.5,
      T.chassisHeight * 0.5,
      T.chassisLength * 0.5,
    )
      .setFriction(0.4)
      .setRestitution(0.0);
    physics.world.createCollider(colliderDesc, this.body);

    // Visuals
    this.root = new TransformNode(`remote-${identity.id}`, scene);
    this.bodyMesh = createChassisMesh(scene, T, identity.color);
    this.bodyMesh.parent = this.root;

    const cornerXs = [T.wheelTrack, -T.wheelTrack, T.wheelTrack, -T.wheelTrack];
    const cornerZs = [T.frontAxle, T.frontAxle, -T.rearAxle, -T.rearAxle];
    for (let i = 0; i < 4; i++) {
      const w = createWheelMesh(scene, T, `remote-${identity.id}-wheel-${i}`);
      w.parent = this.root;
      w.position.set(cornerXs[i]!, T.wheelAnchorY, cornerZs[i]!);
      w.rotationQuaternion = Quaternion.Identity();
      this.wheels.push(w);
    }
  }

  /** Push a fresh snapshot received over the network. */
  pushSnapshot(snap: PlayerSnapshot, worldX: number, worldY: number, worldZ: number): void {
    this.buffer.push(snap, worldX, worldY, worldZ);
  }

  /**
   * Advance the remote car to its interpolated pose at `renderTime`. Also rolls wheels.
   * Returns the current world position (or null if no data yet) so callers can compute distance.
   */
  tick(renderTime: number, dt: number): Vector3 | null {
    if (this.disposed) return null;
    const pose = this.buffer.sample(renderTime);
    if (!pose) return null;

    this.body.setNextKinematicTranslation({ x: pose.px, y: pose.py, z: pose.pz });
    this.body.setNextKinematicRotation({ x: pose.qx, y: pose.qy, z: pose.qz, w: pose.qw });

    // Visuals — set immediately (kinematic position is authoritative).
    this.root.position.set(pose.px, pose.py, pose.pz);
    if (!this.root.rotationQuaternion) this.root.rotationQuaternion = Quaternion.Identity();
    this.root.rotationQuaternion.set(pose.qx, pose.qy, pose.qz, pose.qw);

    // Spin wheels based on speed.
    this.wheelSpin += (pose.speed / VehicleTuning.wheelRadius) * dt;
    const steerY = pose.steer; // already radians
    for (let i = 0; i < this.wheels.length; i++) {
      const w = this.wheels[i]!;
      const isFront = i < 2;
      if (!w.rotationQuaternion) w.rotationQuaternion = Quaternion.Identity();
      // Front wheels apply steer (Y) then spin (X). We approximate by composing.
      const steer = isFront ? steerY : 0;
      const qSteer = Quaternion.RotationAxis(new Vector3(0, 1, 0), steer);
      const qSpin = Quaternion.RotationAxis(new Vector3(1, 0, 0), this.wheelSpin);
      qSteer.multiplyToRef(qSpin, w.rotationQuaternion);
    }

    return new Vector3(pose.px, pose.py, pose.pz);
  }

  /** Last known world position (no extrapolation), or null. */
  lastKnownPosition(): Vector3 | null {
    const latest = this.buffer.latest();
    if (!latest) return null;
    return new Vector3(latest.worldX, latest.worldY, latest.worldZ);
  }

  /** Last known forward direction (xz unit vector), or null. */
  lastKnownForward(): { x: number; z: number } | null {
    const latest = this.buffer.latest();
    if (!latest) return null;
    const q = latest.snap;
    // Apply quat to local +Z = forward.
    const x = 2 * (q.qx * q.qz + q.qw * q.qy);
    const z = 1 - 2 * (q.qx * q.qx + q.qy * q.qy);
    const n = Math.hypot(x, z) || 1;
    return { x: x / n, z: z / n };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    try {
      this.physics.world.removeRigidBody(this.body);
    } catch {
      // ignore
    }
    this.root.dispose(false, true);
  }
}
